import { useState } from 'react'
import { Button, EmptyNote, Icons, Loading, StateNote, StatusLabel } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useClients } from '@/lib/api/hooks'
import { navigate } from '@/app/router'
import { PageFrame, Section } from '@/shell/PageFrame'
import { CLIENT_STATUS } from './clientStatus'
import { ClientCreateDialog } from './ClientCreateDialog'

/**
 * The client registry. A list, an Add button, and rows that open into the
 * client's page — the registry is a doorway, not a place to work in; the
 * work happens on the record. Adding a client raises the invitation card —
 * one question at a time over the registry — the same shape every other
 * "bring something in" moment in the console uses.
 */
export function ClientsPage() {
  const clients = useClients()
  const [adding, setAdding] = useState(false)

  return (
    <PageFrame title="Clients" description="Who the studio works for.">
      <Section
        title="Registry"
        aside={
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            Add client
          </Button>
        }
      >
        {clients.isLoading ? (
          <Loading label="Loading clients" />
        ) : clients.error ? (
          <StateNote
            title="The registry did not load."
            detail={describeError(clients.error)}
            action={
              <Button size="sm" icon={<Icons.retry />} onClick={() => void clients.refetch()}>
                Try again
              </Button>
            }
          />
        ) : (clients.data?.length ?? 0) === 0 ? (
          <EmptyNote
            title="No clients yet."
            detail="The first client of the studio is one button away."
            action={
              <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setAdding(true)}>
                Add client
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col">
            {clients.data!.map((client) => (
              <li key={client.id} className="border-b border-hairline last:border-0">
                <button
                  type="button"
                  onClick={() => navigate({ name: 'client', id: client.id })}
                  className="-mx-2 flex w-full items-center gap-3 rounded-md px-2 py-3 text-left transition-colors duration-fast hover:bg-tint"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ui font-medium text-foreground">{client.name}</span>
                    <span className="block truncate text-caption text-muted-foreground">
                      {[
                        client.companyName,
                        client.discordServerName ? `${client.discordServerName} · Discord` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </span>
                  </span>
                  {client.rating !== null ? (
                    <span className="font-display text-[15px] text-muted-foreground" title="Your rating">
                      {client.rating}
                    </span>
                  ) : null}
                  <StatusLabel tone={CLIENT_STATUS[client.status].tone} label={CLIENT_STATUS[client.status].label} />
                  <span className="inline-flex rotate-90 items-center text-muted-foreground">
                    <Icons.disclosure />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <ClientCreateDialog open={adding} onClose={() => setAdding(false)} />
    </PageFrame>
  )
}
